import { useEffect, useState } from "react";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import ComboPanel from "@/components/admin/ComboPanel";
import MenuItemDialog from "@/components/admin/MenuItemDialog";
import MenuCategoriesTab from "@/components/admin/menu/MenuCategoriesTab";
import MenuGroupsTab from "@/components/admin/menu/MenuGroupsTab";
import MenuMasterGroupsTab from "@/components/admin/menu/MenuMasterGroupsTab";
import VariationsEditor from "@/components/admin/menu/VariationsEditor";
import { TD, TH } from "@/components/admin/menu/shared";
import { useMenuAdmin } from "@/hooks/useMenuAdmin";

/**
 * The menu hub: one place for items, the categories, groups and master groups
 * (Food / Beverages / Extras) they sit in, and combos (DESIGN.md §6.6). Before it the module was one item table; categories,
 * groups and variations had working, audited endpoints and no screen.
 *
 * The tab lives in the hash (`#m/menu/categories`) so a reload or a pasted link
 * lands on the same tab (DESIGN.md §6.1).
 */
const TABS = [
  ["items", "Items"],
  ["categories", "Categories"],
  ["groups", "Groups"],
  ["masters", "Master Groups"],
  ["combos", "Combos"],
];

const tabFromHash = () => {
  const part = (window.location.hash || "").replace(/^#/, "").split("/")[2];
  return TABS.some(([id]) => id === part) ? part : "items";
};

const VEG_DOT = { veg: "bg-success", egg: "bg-warning", non_veg: "bg-danger" };
const VEG_LABEL = { veg: "Veg", egg: "Egg", non_veg: "Non-veg" };

export default function MenuHub({ token, readOnly = false }) {
  const data = useMenuAdmin(token);
  const [tab, setTab] = useState(tabFromHash);

  useEffect(() => {
    const hash = tab === "items" ? "#m/menu" : `#m/menu/${tab}`;
    if (window.location.hash !== hash) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${hash}`);
    }
  }, [tab]);

  const categories = data.categories || [];
  const itemCount = categories.reduce((total, category) => total + (category.items || []).length, 0);
  const counts = {
    items: itemCount,
    categories: categories.length,
    groups: data.groups?.length ?? 0,
    masters: data.masters?.length ?? 0,
  };

  return (
    <div className="space-y-3" data-testid="menu-management-panel">
      <div className="flex items-end justify-between gap-3 border-b" role="tablist" aria-label="Menu">
        <div className="flex">
          {TABS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={`-mb-px flex h-9 items-center gap-1.5 border-b-2 px-3 text-body font-medium transition-colors ${
                tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`menu-tab-${id}`}
            >
              {label}
              {counts[id] != null && data.categories && <span className="text-meta tabular-nums opacity-70">{counts[id]}</span>}
            </button>
          ))}
        </div>
      </div>

      {data.error && (
        <p className="rounded-md border border-danger/30 bg-danger-subtle px-3 py-2 text-meta text-danger" role="alert" data-testid="menu-hub-error">
          Could not load the menu: {data.error}
        </p>
      )}

      {tab === "items" && <ItemsTab data={data} readOnly={readOnly} />}
      {tab === "categories" && <MenuCategoriesTab data={data} readOnly={readOnly} />}
      {tab === "groups" && <MenuGroupsTab data={data} readOnly={readOnly} />}
      {tab === "masters" && <MenuMasterGroupsTab data={data} readOnly={readOnly} onOpenTab={setTab} />}
      {tab === "combos" && <ComboPanel token={token} />}
    </div>
  );
}

const NEW_ITEM = Symbol("new-item");

function ItemsTab({ data, readOnly }) {
  const [query, setQuery] = useState("");
  const [editingId, setEditingId] = useState(null);

  if (!data.categories) return <p className="px-3 py-6 text-meta text-muted-foreground">Loading menu…</p>;

  const all = data.categories.flatMap(category =>
    (category.items || []).map(item => ({ ...item, category }))
  );
  const q = query.trim().toLowerCase();
  const shown = !q
    ? all
    : all.filter(item =>
        [item.name, item.short_name, item.category.name].some(field => field && String(field).toLowerCase().includes(q))
      );

  // The dialog edits the live row, so a variation added or saved shows at once.
  const editing = editingId === NEW_ITEM ? null : all.find(item => item.id === editingId) || null;
  const pickable = data.categories.filter(category => category.is_active !== false || category.id === editing?.category_id);

  return (
    <div className="space-y-2" data-testid="menu-items-tab">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative">
          <Search className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
          <input
            className="h-8 w-72 rounded-md border bg-background pl-8 pr-2 text-body outline-none focus:border-primary"
            placeholder="Search item, short code or category"
            value={query}
            onChange={event => setQuery(event.target.value)}
            aria-label="Search items"
            data-testid="menu-item-search-input"
          />
        </label>
        {q && <span className="text-meta tabular-nums text-muted-foreground">Showing {shown.length} of {all.length}</span>}
        {!readOnly && (
          <Button variant="action" size="sm" className="ml-auto" onClick={() => setEditingId(NEW_ITEM)} data-testid="add-menu-item-button">
            <Plus className="h-4 w-4" />Add Item
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-md border bg-card">
        <table className="w-full">
          <thead className="sticky top-0 border-b bg-muted/40">
            <tr>
              <th className={TH}>Item</th>
              <th className={TH}>Category</th>
              <th className={TH}>Short code</th>
              <th className={`${TH} text-right`}>Variations</th>
              <th className={`${TH} text-right`}>Price</th>
              <th className={TH}>Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {shown.map(item => {
              const activeVariations = (item.variations || []).filter(v => v.is_active).length;
              return (
                <tr key={item.id} className="h-10 cursor-pointer hover:bg-muted/40" onClick={() => setEditingId(item.id)} data-testid={`menu-item-row-${item.id}`}>
                  <td className={TD}>
                    <span className="inline-flex items-center gap-2">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${VEG_DOT[item.attribute] || "bg-muted-foreground"}`} aria-hidden="true" />
                      <span className="font-medium">{item.name}</span>
                      <span className="sr-only">{VEG_LABEL[item.attribute]}</span>
                    </span>
                  </td>
                  <td className={`${TD} text-muted-foreground`}>
                    {item.category.name}
                    {item.category.is_active === false && <span className="ml-1 text-meta">(hidden)</span>}
                  </td>
                  <td className={`${TD} text-muted-foreground`}>{item.short_name || "—"}</td>
                  <td className={`${TD} text-right tabular-nums text-muted-foreground`}>{(item.variations || []).length ? `${activeVariations} of ${(item.variations || []).length}` : "—"}</td>
                  <td className={`${TD} text-right tabular-nums`}>₹{Number(item.price).toFixed(2)}</td>
                  <td className={TD}>
                    <span className="inline-flex items-center gap-1.5 text-body">
                      <span className={`h-1.5 w-1.5 rounded-full ${item.is_available ? "bg-success" : "bg-muted-foreground"}`} aria-hidden="true" />
                      {item.is_available ? "On sale" : "Off"}
                    </span>
                  </td>
                </tr>
              );
            })}
            {shown.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-meta text-muted-foreground" data-testid={q ? "menu-item-search-empty" : "menu-item-empty"}>
                  {q ? `No item matches "${query.trim()}".` : "No menu items yet. Add the first one above."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingId != null && (
        <MenuItemDialog
          key={editingId === NEW_ITEM ? "new" : editingId}
          item={editing}
          categories={pickable}
          onSave={data.saveItem}
          onClose={() => setEditingId(null)}
          readOnly={readOnly}
          onPhotoChanged={() => data.reload()}
        >
          <VariationsEditor item={editing} data={data} readOnly={readOnly} />
        </MenuItemDialog>
      )}
    </div>
  );
}
